// Faixa da balança dentro do quadro de peso (QuantidadePeso), só para produto
// "Pesar na balança" num módulo e computador com balança (useBalanca).
// - Manual: botão "Ler balança" — espera o peso firmar e preenche.
// - Automático: mostra o peso ao vivo; quando firma, preenche sozinho (e de
//   novo se o prato mudar e firmar outra vez).
// O peso que veio da balança trava os botões e o campo; "Digitar à mão" solta
// (menos no Totem: no autoatendimento o cliente não digita o peso).
import { Scale } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { MENSAGEM_ESTADO, pesoLiquido } from "./protocolo";
import { escutar, lerPesoFirme, MENSAGEM_CONEXAO } from "./servico";
import { useBalanca } from "./useBalanca";

const kgTexto = (kg) => `${Number(kg || 0).toFixed(3).replace(".", ",")} kg`;

const CORES = {
  escuro: { caixa: "bg-zinc-900 border-zinc-700", texto: "text-zinc-200", fraco: "text-zinc-400", botao: "bg-zinc-700 hover:bg-zinc-600 text-zinc-100" },
  painel: { caixa: "bg-muted/40 border-border", texto: "text-foreground", fraco: "text-muted-foreground", botao: "bg-muted hover:bg-muted/70 text-foreground" },
  claro: { caixa: "bg-white border-gray-200", texto: "text-gray-900", fraco: "text-gray-500", botao: "bg-gray-100 hover:bg-gray-200 text-gray-900" },
};

function mensagem(leitura) {
  if (!leitura) return "";
  return MENSAGEM_CONEXAO[leitura.estado] || MENSAGEM_ESTADO[leitura.estado] || "";
}

export default function LeitorBalanca({ modulo, product, setQuantity, pesado, setPesado, variante = "escuro" }) {
  const cor = CORES[variante] || CORES.escuro;
  const { ativa, modo, minimoKg, taraGramas } = useBalanca({ modulo, produto: product });
  const [aviso, setAviso] = useState("");
  const [aoVivo, setAoVivo] = useState(null);
  const [lendo, setLendo] = useState(false);
  const [aMao, setAMao] = useState(false);
  const ultimoAplicado = useRef(null);

  const aplicar = (kg) => {
    if (kg < minimoKg) { setAviso(`Peso abaixo do mínimo (${Math.round(minimoKg * 1000)} g)`); return false; }
    const liquido = pesoLiquido(kg, taraGramas);
    if (liquido <= 0) { setAviso("O peso não passa do recipiente cadastrado no produto"); return false; }
    setQuantity(liquido);
    setPesado(true);
    ultimoAplicado.current = kg;
    setAviso("");
    return true;
  };

  // Modo automático: escuta enquanto o quadro está aberto
  useEffect(() => {
    if (!ativa || modo !== "automatico" || aMao) return undefined;
    return escutar(({ leitura, firmeKg }) => {
      setAoVivo(leitura);
      if (firmeKg != null && firmeKg !== ultimoAplicado.current) aplicar(firmeKg);
    });
  }, [ativa, modo, aMao]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ativa) return null;

  const lerAgora = async () => {
    setLendo(true);
    setAviso("");
    setAMao(false);
    const l = await lerPesoFirme();
    setLendo(false);
    if (l.estado === "ok" && l.firme) aplicar(l.kg);
    else setAviso(l.estado === "ok" ? MENSAGEM_ESTADO.instavel : mensagem(l));
  };

  const soltar = () => { setAMao(true); setPesado(false); ultimoAplicado.current = null; };

  const desconto = taraGramas > 0 ? `Recipiente de ${taraGramas} g descontado` : "";
  const linhaAoVivo = aoVivo && aoVivo.estado === "ok" ? `Na balança: ${kgTexto(aoVivo.kg)}` : mensagem(aoVivo);
  const mostraLer = modo === "manual" || aMao;
  const podeDigitar = modulo !== "Totem";
  const linha2 = aviso
    || (modo === "automatico" && !aMao ? linhaAoVivo || "Lendo a balança…"
      : pesado ? desconto || "Para mudar, pese de novo" : "Ponha o produto e toque em Ler balança");

  return (
    <div className={`rounded-lg border px-3 py-2 space-y-2 ${cor.caixa}`} data-e2e="leitor-balanca">
      <div className="flex items-center gap-3">
        <Scale className={`w-5 h-5 shrink-0 ${pesado ? "text-emerald-500" : cor.fraco}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${cor.texto}`} data-e2e={pesado ? "balanca-pesado" : undefined}>
            {pesado ? "Pesado na balança" : modo === "automatico" && !aMao ? "Coloque na balança" : "Balança"}
          </p>
          <p className={`text-xs ${aviso ? "text-amber-500" : cor.fraco}`} data-e2e="balanca-aviso">{linha2}</p>
        </div>
      </div>
      {(mostraLer || (pesado && podeDigitar)) && (
        <div className={`grid gap-2 ${mostraLer && pesado && podeDigitar ? "grid-cols-2" : "grid-cols-1"}`}>
          {mostraLer && (
            <button type="button" onClick={lerAgora} disabled={lendo} data-e2e="balanca-ler"
              className="h-9 px-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold disabled:opacity-60">
              {lendo ? "Lendo…" : pesado ? "Pesar de novo" : "Ler balança"}
            </button>
          )}
          {pesado && podeDigitar && (
            <button type="button" onClick={soltar} data-e2e="balanca-digitar"
              className={`h-9 px-3 rounded-lg text-sm font-semibold ${cor.botao}`}>Digitar à mão</button>
          )}
        </div>
      )}
    </div>
  );
}
